import React, { createContext, useEffect, useState } from 'react';
import SurveyGroup from './SurveyGroup';
import { Button, Col, Container, Form, Input, Row } from 'reactstrap';
import axios from '../../axios-base';
import ReCarousel from '../../components/UI/ReCarousel/ReCarousel';
import debounce from 'lodash.debounce';
import SurveyPagination from './SurveyPagination';
import SurveySuccess from './SurveySuccess';
import { useLocation } from 'react-router-dom';
// import test_data from "./test_data.json"

import styles from './Survey.module.scss';

export const AnswersContext = createContext(null);

function createOrGetAnswerId() {
    // Function to get a cookie by name
    function getCookie(name) {
        const value = '; ' + document.cookie;
        const parts = value.split('; ' + name + '=');
        if (parts.length === 2) return parts.pop().split(';').shift();
    }

    const cookieName = 'survey-answer-id';
    let cookieValue = getCookie(cookieName);

    // If the cookie does not exist, create it
    if (!cookieValue) {
        cookieValue = crypto.randomUUID(); // Generate a UUID
        const date = new Date();
        date.setTime(date.getTime() + 7 * 24 * 60 * 60 * 1000); // Cookie expires after 1 week
        const expires = '; expires=' + date.toUTCString();
        document.cookie = cookieName + '=' + cookieValue + expires + '; path=/';
    }
    // Return the cookie value
    return cookieValue;
}

function confirmAnswers(answers, id, confirmed, onSuccess) {
    // Answer id in DB
    id = createOrGetAnswerId();

    axios
        .put('/surveys/answer', { _id: id, answers: answers, confirmed: confirmed })
        .then((response) => {
            if (onSuccess) onSuccess();
        })
        .catch((e) => console.log(e));
}

export default function Survey() {
    const location = useLocation();
    const [surveyData, setSurveyData] = useState({ title: 'Umfrage wird geladen...', questions: [] });
    const [answers, setAnswers] = useState({});
    const [, setClickHandler] = useState(null);
    const [desiredSlide, setDesiredSlide] = useState(0);
    const [actualSlide, setActualSlide] = useState(desiredSlide);
    const [formSubmitted, setFormSubmitted] = useState(false); // State to track form submission
    const submitAnswer = (id, value) => setAnswers({ ...answers, [id]: value });
    const isMobile = (e) => window.innerWidth < 768;
    const [mobile, setMobile] = useState(isMobile());
    const slides = surveyData ? surveyData.questions.length : 0;

    useEffect(() => {
        axios
            .get('/surveys/questions')
            .then((res) => setSurveyData(res.data))
            .catch((_) => setSurveyData(null));
        // setSurveyData(test_data)
    }, []);

    useEffect(() => {
        const debouncedResizeHandler = debounce(() => {
            setMobile(isMobile());
        }, 100);

        window.addEventListener('resize', debouncedResizeHandler);

        return () => window.removeEventListener('resize', debouncedResizeHandler);
    }, []);

    // render absolutely nothing if we have no questions. All other logic from here on out works on the assumption that
    // there are questions.
    if (!surveyData || !surveyData.questions || surveyData?.questions?.length === 0) return null;

    let surveyDisplay;

    if (mobile) {
        surveyDisplay = (
            <div className={styles.CarouselOuterWrapper}>
                <SurveyPagination currentPage={actualSlide} pages={slides} onPageChange={setDesiredSlide} />
                <div className={styles.CarouselInnerWrapper}>
                    <ReCarousel
                        axis={'x'}
                        className="re-carousel"
                        setClick={(click) => setClickHandler(click)}
                        desiredSlide={desiredSlide}
                        onStartFrameIndex={desiredSlide}
                        onTransitionEnd={(state, ms) => {
                            setDesiredSlide(state);
                            setActualSlide(state);
                        }}
                    >
                        {surveyData.questions.map((question, index) => {
                            if (question.type === 'questionGroup') {
                                return (
                                    <div key={index}>
                                        <SurveyGroup questions={question.data} style={{ height: 'max-content' }} />
                                    </div>
                                );
                            } else {
                                return (
                                    <div key={index}>
                                        <SurveyGroup questions={[question]} style={{ height: 'max-content' }} />
                                    </div>
                                );
                            }
                        })}
                    </ReCarousel>
                </div>
            </div>
        );
    } else {
        surveyDisplay = (
            <Row className={`gy-3 ${styles.QuestionsRow}`}>
                {surveyData.questions.map((question, index) => {
                    return (
                        <Col md={12} lg={6} xl={4} key={index} className="d-flex">
                            <SurveyGroup questions={question.type === 'questionGroup' ? question.data : [question]} />
                        </Col>
                    );
                })}
            </Row>
        );
    }

    return (
        <>
            <h1>{surveyData.title}</h1>
            <a href="#survey-skip" className="visually-hidden-focusable anl_survey--skip-link">
                Umfrage überspringen
            </a>
            <AnswersContext.Provider value={{ answers, submitAnswer, isMobile: mobile }}>
                <Form
                    method="PUT"
                    className="anl_survey--form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        confirmAnswers(answers, location.state, true, () => setFormSubmitted(true));
                    }}
                >
                    {formSubmitted ? (
                        <SurveySuccess /> // Render SurveySuccess component upon successful submission
                    ) : (
                        <>
                            {surveyDisplay}

                            <div className={styles.SurveyControlButtonContainer}>
                                {!mobile ? (
                                    <Input
                                        className="btn btn-secondary w-auto float-end anl_survey--submit-btn"
                                        type="submit"
                                        value="Jetzt absenden"
                                    />
                                ) : (
                                    <Container>
                                        <Row>
                                            <Col xs={6} className="ps-0 pe-3">
                                                {desiredSlide >= 1 ? (
                                                    <Button
                                                        outline={true}
                                                        className="w-100 anl_survey--back-btn"
                                                        color="dark"
                                                        onClick={() => setDesiredSlide(desiredSlide - 1)}
                                                    >
                                                        Zurück
                                                    </Button>
                                                ) : null}
                                            </Col>
                                            <Col xs={6} className="pe-0 ps-3">
                                                {desiredSlide < slides - 1 ? (
                                                    <Button
                                                        className="w-100 anl_survey--next-btn"
                                                        onClick={() => {
                                                            setDesiredSlide(desiredSlide + 1);
                                                            confirmAnswers(answers, location.state, false);
                                                        }}
                                                    >
                                                        Weiter
                                                    </Button>
                                                ) : (
                                                    <Input
                                                        className="btn btn-primary w-100 anl_survey--submit-btn"
                                                        type="submit"
                                                        value="Jetzt absenden"
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    </Container>
                                )}
                            </div>
                        </>
                    )}
                </Form>
            </AnswersContext.Provider>
            <div id="survey-skip" />
        </>
    );
}
